// Admin Home. Rev 3: MOCKUP_HOME_REV3.html (approved 2026-10-23, 9:00 PM). Words: COHESION_WORDS.md.
// Home is where you pick your site. Nothing on Home mixes sites (Robert, 8:52 PM).
// People, gloves, training and records live in each site's hub. Home keeps the work that is the
// same at every site (Program Setup) and the Field Pages.
// Earlier: ADMIN_HOME_WORDS.md, MOCKUP_ADMIN_HOME_APPROVED.html (2026-10-08).
//
// Rules:
// * Adds no data and no new jobs. Every count is worked out the way its own page works it out.
// * Amber count only for something that needs action. Never a plain total.
// * Planned pages not built yet are one line at the bottom, not tiles.
// * Field Pages open what a QEW sees, in a new tab.
// Pure: data in, tiles out. The page loads the data the same way each page does.

import { withAdminBack } from "./admin-way-back";
import { emailTileLine, failedThisWeek, type EmailRow } from "./email";
import { sopLibrary, type SopDoc } from "./resources";
import { stageOfStrip, type LastStep, type SiteStage } from "./site-hub";
import { needsPlantManager, sortSites, type Contact, type Site } from "./sites";

export type TileState = "live" | "coming" | "parked";

export interface Tile {
  code: string;
  name: string;
  state: TileState;
  /** Where a live tile goes. Blank on grey tiles and on the Guest Page tile (it opens a sheet). */
  href: string;
  /** Opens in a new tab (Field Pages). */
  newTab: boolean;
  /** Guest Page opens the site and label picker. */
  opensSheet: boolean;
  /** The grey line under the name. "…" while the page's data loads. */
  line: string;
  /** Amber corner count. 0 shows nothing. */
  alert: number;
}

export interface TileGroup {
  title: string;
  tiles: Tile[];
}

/** What Home reads. Each part is null until its page data has loaded. */
export interface HomeInput {
  today: string;
  sites: { sites: Site[]; assetCounts: Record<string, number>; contacts: Contact[]; demo?: Record<string, unknown> } | null;
  people: { badges: { "Badge ID": string; "Holder Name"?: string; Status?: string }[] } | null;
  tasks: { tasks: { taskId: string; name: string; tier: number; isActive: boolean; sopNumber: string }[]; sops: SopDoc[] } | null;
  settings: { programContacts: unknown[] } | null;
  lists: { classes: unknown[]; plants: unknown[] } | null;
  refs: { refs: { status: string }[] } | null;
  training: { quizzes: unknown[]; questions: unknown[]; qewTags: unknown[] } | null;
  /** The Email Log's newest emails, as its page reads them. */
  emails?: { rows: EmailRow[] } | null;
  /** Now, for the 7-day failed count. Defaults to midday of today. */
  nowIso?: string;
  /** Each site's steps as of the last time its hub was opened on this device (stripOf). The fallback. */
  strips?: Record<string, string>;
  /** Tile codes whose data could not be read. The tile says so; its page offers Try Again. */
  failed?: string[];
}

const LOADING = "…";
const QEW_TAG = /^\d{2}-QEW\d{2}$/;

const n = (count: number, one: string, many = `${one}s`) => `${count.toLocaleString("en-US")} ${count === 1 ? one : many}`;

function live(code: string, name: string, href: string, line: string, alert = 0): Tile {
  return { code, name, state: "live", href, newTab: false, opensSheet: false, line, alert };
}

function field(code: string, name: string, href: string, line: string, opensSheet = false): Tile {
  // from=admin: the field page shows a way back to Admin Home (admin-way-back.ts).
  return { code, name, state: "live", href: withAdminBack(href), newTab: true, opensSheet, line, alert: 0 };
}

/** Active QEW tags: same set the Gloves roster and People & Badges count. Admin badge left out. */
function activeQews(people: NonNullable<HomeInput["people"]>): number {
  return people.badges.filter((b) => b.Status === "Active" && QEW_TAG.test(b["Badge ID"])).length;
}

/** Email Log: failed in the last 7 days (EMAIL_SENDER_WORDS.md). */
export function emailTile(d: HomeInput["emails"], nowIso: string): Tile {
  if (!d) return live("EM", "Email Log", "#/email-log/all", LOADING);
  const failed = failedThisWeek(d.rows, nowIso);
  return live("EM", "Email Log", "#/email-log/all", emailTileLine(d.rows, failed), failed);
}

export function tasksTile(d: HomeInput["tasks"]): Tile {
  if (!d) return live("TK", "Tasks", "#/tasks", LOADING);
  const on = d.tasks.filter((t) => t.isActive).length;
  return live("TK", "Tasks", "#/tasks", `${on} on · ${d.tasks.length - on} off`);
}

/** The SOP Library a QEW sees in Resources: SOPs of switched-on tasks, plus the permit. */
export function sopCount(d: NonNullable<HomeInput["tasks"]>): number {
  return sopLibrary(d.sops, d.tasks).total;
}

export function homeGroups(i: HomeInput): TileGroup[] {
  const failed = new Set(i.failed ?? []);
  return baseGroups(i).map((g) => ({
    ...g,
    tiles: g.tiles.map((t) => (failed.has(t.code) && t.line === LOADING ? { ...t, line: "Not loaded · open to retry" } : t)),
  }));
}

function baseGroups(i: HomeInput): TileGroup[] {
  const t = i.training;
  const demo = i.sites ? Object.keys(i.sites.demo ?? {}).length : null;
  return [
    {
      title: "Program Setup",
      tiles: [
        live("PS", "Program Settings", "#/settings", i.settings ? n(i.settings.programContacts.length, "program contact") : LOADING),
        tasksTile(i.tasks),
        live("LS", "Lists", "#/lists", i.lists ? `${n(i.lists.classes.length, "class", "classes")} · ${n(i.lists.plants.length, "plant")}` : LOADING),
        live("QB", "Quiz Bank", "#/quiz-bank/quiz1", t ? `${n(t.quizzes.length, "quiz", "quizzes")} · ${n(t.questions.length, "question")}` : LOADING),
        live("RL", "Reference Library", "#/references", i.refs ? (i.refs.refs.length ? `${i.refs.refs.filter((r) => r.status === "Current").length} current` : "Empty") : LOADING),
        emailTile(i.emails ?? null, i.nowIso ?? `${i.today}T12:00:00.000Z`),
        // The Demo Set is the same at every site, so it is program setup (it lived on the Sites page).
        live("DS", "Demo Set", "#/sites/demo", demo === null ? LOADING : `${n(demo, "template")} · used at every site`),
      ],
    },
    {
      title: "Field Pages",
      tiles: [
        field("GP", "Guest Page", "", "Demo label, any site", true),
        field("Q1", "Quiz 1", "/?quiz=1", "Node 1 link"),
        field("Q2", "Quiz 2", "/?quiz=2", "Node 2 link"),
        field("Q3", "Quiz 3", "/?quiz=3", "Node 3 link"),
        field("SL", "SOP Library", "/#/sop-library", i.tasks ? `${n(sopCount(i.tasks), "SOP")} · review` : LOADING),
      ],
    },
  ];
}

/** Planned pages, one line at the bottom of Home. */
export const COMING_LINE = "Coming: JSP Records, EEWP, LOTO, Briefings.";

// ---- The Sites list on Home ----

export interface HomeSiteRow {
  siteNumber: string;
  name: string;
  /** "Baconton, GA · 137 assets". */
  line: string;
  isActive: boolean;
  noPlantManager: boolean;
  /** Null until the site's hub has been opened on this device. */
  stage: SiteStage | null;
  /** Lower case name, number and place, for the search box. */
  search: string;
}

export function homeSites(i: HomeInput): HomeSiteRow[] | null {
  if (!i.sites) return null;
  const d = i.sites;
  const strips = i.strips ?? {};
  return sortSites(d.sites).map((s) => {
    const place = [s.city, s.state].filter(Boolean).join(", ");
    const line = [place, n(d.assetCounts[s.siteNumber] ?? 0, "asset")].filter(Boolean).join(" · ");
    return {
      siteNumber: s.siteNumber,
      name: s.name,
      line,
      isActive: s.isActive,
      noPlantManager: needsPlantManager(s, d.contacts),
      // The site's own stage first (every device), then this device's last look (STAGE_CLOUD_WORDS.md).
      stage: stageOfStrip(s.stage?.strip) ?? stageOfStrip(strips[s.siteNumber]),
      search: `${s.siteNumber} ${s.name} ${place}`.toLowerCase(),
    };
  });
}

/** "6 sites · 881 assets". */
export function sitesHeadLine(i: HomeInput): string {
  if (!i.sites) return "";
  const assets = Object.values(i.sites.assetCounts).reduce((a, b) => a + b, 0);
  return `${n(i.sites.sites.length, "site")} · ${n(assets, "asset")}`;
}

/** Continue on Home: the newest step worked at any site on this device. */
export function homeContinue(last: Record<string, LastStep>, siteNames: Record<string, string>): (LastStep & { site: string; siteName: string }) | null {
  const best = Object.entries(last)
    .filter(([, v]) => v && typeof v.href === "string" && typeof v.at === "string")
    .sort((a, b) => (a[1].at < b[1].at ? 1 : -1))[0];
  if (!best) return null;
  const [site, v] = best;
  return { ...v, site, siteName: siteNames[site] ?? `Site ${site}` };
}

/** "Admin · 5 sites · 21 Active QEWs". */
export function homeSubLine(i: HomeInput): string {
  const parts = ["Admin"];
  if (i.sites) parts.push(n(i.sites.sites.length, "site"));
  // Same count People & Badges shows once people are read; the training read is there first.
  const qews = i.people ? activeQews(i.people) : i.training ? i.training.qewTags.length : null;
  if (qews !== null) parts.push(`${qews} Active ${qews === 1 ? "QEW" : "QEWs"}`);
  return parts.join(" · ");
}

/** Two letters from a name: Robert Stuart reads RS. */
export function initialsOf(name: string): string {
  const w = name.split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] ?? "") + (w.length > 1 ? (w[w.length - 1]?.[0] ?? "") : "")).toUpperCase() || "AD";
}

// ---- Guest Page sheet ----

export const DEMO_LABELS = [
  { key: "mcc", label: "MCC Bucket", suffix: "-ER1-MCC-25-C4" },
  { key: "msd", label: "MSD", suffix: "-EA1-MSD-25" },
  { key: "panel", label: "Panel", suffix: "-LC1-PNL-25" },
] as const;
export type DemoLabelKey = (typeof DEMO_LABELS)[number]["key"];

/** "01" + MSD reads 01-EA1-MSD-25. Same suffixes the guest page opens (guest-asset.ts). */
export function demoLabelId(site: string, key: DemoLabelKey): string {
  const d = DEMO_LABELS.find((x) => x.key === key) ?? DEMO_LABELS[1];
  return `${site}${d.suffix}`;
}

export function guestUrl(site: string, key: DemoLabelKey): string {
  return withAdminBack(`/?aid=${encodeURIComponent(demoLabelId(site, key))}`);
}
